import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CarouselItem, type MediaAsset } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MediaThumb } from "@/components/MediaThumb";
import CaptionChatPanel from "@/components/CaptionChatPanel";
import { StudioOptionCard } from "@/components/DesignReviewPanel";
import { StudioSplit } from "@/components/studioTabs";
import { cn } from "@/lib/utils";

function fmt(sec: number) {
  const n = Math.max(0, Number(sec) || 0);
  const m = Math.floor(n / 60);
  const s = (n - m * 60).toFixed(1);
  return m ? `${m}:${s.padStart(4, "0")}` : `${Number(s).toFixed(1)}s`;
}

export default function CzasotekstPanel({
  item,
  caption,
  onCaption,
  onHook,
}: {
  item: CarouselItem;
  caption: string;
  onCaption: (v: string) => void;
  onHook: (v: string) => void;
}) {
  const qc = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const clip = (item.copy as { czasotekst?: Record<string, unknown> } | undefined)?.czasotekst || {};
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<MediaAsset | null>(null);
  const [inSec, setInSec] = useState(Number(clip.in_sec || 0));
  const [outSec, setOutSec] = useState(Number(clip.out_sec || 6));
  const [scale, setScale] = useState(Number(clip.scale || 1));
  const [panX, setPanX] = useState(Number(clip.x || 0));
  const [panY, setPanY] = useState(Number(clip.y || 0));
  const [loopSec, setLoopSec] = useState(Number(clip.loop_sec || 7));
  const [hook, setHook] = useState(String((item.copy as { hook_on_screen?: string })?.hook_on_screen || item.hook || ""));
  const listQ = useQuery({
    queryKey: ["media-reels", q],
    queryFn: () => api.mediaList({ kind: "video", q }),
  });
  const reels = useMemo(() => listQ.data?.items || [], [listQ.data]);
  const ffmpeg = listQ.data?.ffmpeg !== false;

  const pickAsset = (asset: MediaAsset) => {
    setPicked(asset);
    const dur = Number(asset.duration_sec || 0);
    setInSec(0);
    setOutSec(dur >= 3 ? Math.min(dur, 6) : Math.max(dur, 0.2));
  };

  const applyCopyResult = (data: CarouselItem) => {
    qc.setQueryData(["carousel", item.id], data);
    const nextHook = String((data.copy as { hook_on_screen?: string })?.hook_on_screen || data.hook || "");
    setHook(nextHook);
    onHook(nextHook);
    onCaption(data.caption || "");
  };

  const copyM = useMutation({
    mutationFn: (feedback: string) => api.czasotekstCopy(item.id, feedback),
    onSuccess: applyCopyResult,
  });

  const chatM = useMutation({
    mutationFn: ({ message, apply = false }: { message: string; apply?: boolean }) =>
      api.czasotekstChat(item.id, message, apply),
    onSuccess: applyCopyResult,
  });

  const undoChatM = useMutation({
    mutationFn: () => api.carouselCaptionChatUndo(item.id),
    onSuccess: applyCopyResult,
  });

  const generating = copyM.isPending || item.llm_status === "czasotekst_copy";
  const chatting = chatM.isPending || item.llm_status === "czasotekst_chat";
  const writing = generating || chatting;
  const chatMessages = Array.isArray(item.discussion?.caption_chat) ? item.discussion.caption_chat : [];
  const canUndoChat = Boolean(item.discussion?.caption_chat_undo);
  const pending = item.discussion?.caption_chat_pending || null;
  const pendingHook = String(pending?.hook_on_screen || "").trim();
  const pendingCaption = String(pending?.caption || "").trim();
  const pendingEngage = String(pending?.captionEngage || "").trim();
  const hasPending = Boolean(pendingHook || pendingCaption || pendingEngage);

  useEffect(() => {
    if (writing) return;
    const next = String((item.copy as { hook_on_screen?: string } | undefined)?.hook_on_screen || item.hook || "");
    if (next) setHook(next);
  }, [item.id, item.hook, item.copy, writing]);

  const composeM = useMutation({
    mutationFn: () => {
      if (!picked?.id && !clip.asset_id) throw new Error("wybierz film");
      return api.czasotekstCompose(item.id, {
        asset_id: picked?.id || String(clip.asset_id || ""),
        in_sec: inSec,
        out_sec: outSec,
        scale,
        x: panX,
        y: panY,
        loop_sec: loopSec,
        hook_on_screen: hook,
        compose: true,
      });
    },
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });

  const saveCaptionM = useMutation({
    mutationFn: () =>
      api.carouselPatchCopy(item.id, {
        caption,
        copy: {
          ...(item.copy || {}),
          hook_on_screen: hook,
          caption,
        },
      }),
    onSuccess: (data) => qc.setQueryData(["carousel", item.id], data),
  });

  const approveM = useMutation({
    mutationFn: () => api.carouselApprove(item.id),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      qc.invalidateQueries({ queryKey: ["carousels"] });
    },
  });

  // Szkic z Wejścia: caption bywa pusty, a draft_text już jest. Podpowiedz go w polu opisu.
  useEffect(() => {
    if (caption.trim()) return;
    const draft = String(item.draft_text || "").trim();
    if (draft) onCaption(draft);
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps -- tylko przy nowym itemie

  return (
    <StudioSplit
      left={
        <div className="space-y-4">
          <div id="czasotekst-text">
            <StudioOptionCard
              optional={false}
              title="Tekst czasotekstu"
              hint="Główna treść jest w opisie do czytania. Na ekranie tylko krótki hak."
            >
              <div className="space-y-2">
                <label className="block text-[11px] text-muted-foreground">Hak na ekranie (max ~12 słów)</label>
                <input
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  value={hook}
                  onChange={(e) => {
                    setHook(e.target.value);
                    onHook(e.target.value);
                  }}
                />
                <label className="block text-[11px] text-muted-foreground">Opis (caption) - to czyta widz</label>
                <textarea
                  className="min-h-[160px] w-full rounded border px-2 py-1.5 text-sm"
                  value={caption}
                  onChange={(e) => onCaption(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="brand" disabled={writing} onClick={() => copyM.mutate("")}>
                    {generating ? "Piszę…" : "Wygeneruj hak + opis"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={saveCaptionM.isPending}
                    onClick={() => saveCaptionM.mutate()}
                  >
                    Zapisz tekst
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!caption.trim() || approveM.isPending}
                    onClick={() => approveM.mutate()}
                  >
                    Zatwierdź tekst
                  </Button>
                </div>
                {copyM.isError ? (
                  <p className="text-xs text-destructive">{(copyM.error as Error)?.message || "błąd"}</p>
                ) : null}
              </div>
            </StudioOptionCard>
          </div>

          <div id="czasotekst-film">
            <StudioOptionCard
              optional={false}
              title="Film w tle"
              hint="Wybierz fragment, zbliżenie i pozycję. Render daje pętlę 9:16 pod IG Reels."
            >
              {!ffmpeg ? (
                <p className="text-xs text-destructive">Na serwerze nie ma ffmpeg. Cięcie nie zadziała.</p>
              ) : null}
              <input
                className="mb-2 w-full rounded border px-2 py-1 text-xs"
                placeholder="szukaj w bibliotece wideo"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <div className="grid max-h-48 grid-cols-3 gap-2 overflow-auto">
                {reels.map((asset) => (
                  <button
                    key={asset.id}
                    type="button"
                    className={cn(
                      "overflow-hidden rounded border text-left",
                      (picked?.id || clip.asset_id) === asset.id ? "border-brand" : "border-border",
                    )}
                    onClick={() => pickAsset(asset)}
                  >
                    <MediaThumb asset={asset} />
                    <p className="truncate px-1 py-0.5 text-[10px]">{asset.original_name || asset.id.slice(0, 8)}</p>
                  </button>
                ))}
              </div>
              {picked || clip.asset_id ? (
                <div className="mt-3 space-y-2">
                  {picked ? (
                    <video
                      ref={videoRef}
                      key={picked.id}
                      className="max-h-64 w-full rounded border bg-black object-contain"
                      src={api.mediaFile(picked.id, false)}
                      controls
                      muted
                      playsInline
                    />
                  ) : null}
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="text-[11px]">
                      Start {fmt(inSec)}
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0.1, Number(picked?.duration_sec || outSec))}
                        step={0.1}
                        value={inSec}
                        className="w-full"
                        onChange={(e) => setInSec(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Koniec {fmt(outSec)}
                      <input
                        type="range"
                        min={inSec + 0.2}
                        max={Math.max(inSec + 0.2, Number(picked?.duration_sec || outSec + 1))}
                        step={0.1}
                        value={outSec}
                        className="w-full"
                        onChange={(e) => setOutSec(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Zbliżenie {scale.toFixed(2)}×
                      <input
                        type="range"
                        min={1}
                        max={2.5}
                        step={0.05}
                        value={scale}
                        className="w-full"
                        onChange={(e) => setScale(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Pętla {loopSec.toFixed(0)} s
                      <input
                        type="range"
                        min={3}
                        max={15}
                        step={1}
                        value={loopSec}
                        className="w-full"
                        onChange={(e) => setLoopSec(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Pozycja X {panX}
                      <input
                        type="range"
                        min={-400}
                        max={400}
                        step={10}
                        value={panX}
                        className="w-full"
                        onChange={(e) => setPanX(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Pozycja Y {panY}
                      <input
                        type="range"
                        min={-400}
                        max={400}
                        step={10}
                        value={panY}
                        className="w-full"
                        onChange={(e) => setPanY(Number(e.target.value))}
                      />
                    </label>
                  </div>
                  <Button
                    size="sm"
                    variant="brand"
                    disabled={composeM.isPending || !ffmpeg}
                    onClick={() => composeM.mutate()}
                  >
                    {composeM.isPending ? "Składam MP4…" : "Złóż czasotekst (MP4)"}
                  </Button>
                  {composeM.isError ? (
                    <p className="text-xs text-destructive">{(composeM.error as Error)?.message || "błąd składu"}</p>
                  ) : null}
                  {item.has_reel ? (
                    <video
                      className="max-h-80 w-full rounded border bg-black object-contain"
                      src={api.carouselReelUrl(item.id)}
                      controls
                      playsInline
                    />
                  ) : null}
                </div>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  Wybierz film poniżej albo wgraj w Media → Wideo (albo inbox/rolki).
                </p>
              )}
            </StudioOptionCard>
          </div>
        </div>
      }
      right={
        <div id="czasotekst-chat" className="space-y-3 xl:sticky xl:top-4">
          {hasPending ? (
            <div className="rounded-md border border-violet-200 bg-violet-50/80 p-3 text-sm text-violet-950">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">Propozycja (jeszcze nie wdrożona)</p>
              {pendingHook ? (
                <p className="mt-1">
                  <span className="font-medium">Hak: </span>
                  {pendingHook}
                </p>
              ) : null}
              {pendingCaption ? (
                <p className="mt-1 whitespace-pre-wrap">
                  <span className="font-medium">Opis: </span>
                  {pendingCaption}
                </p>
              ) : null}
              {pendingEngage ? (
                <p className="mt-1 text-xs text-violet-900/80">
                  <span className="font-medium">Engage: </span>
                  {pendingEngage}
                </p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="brand"
                  disabled={writing}
                  onClick={() => chatM.mutate({ message: "wdrażaj", apply: true })}
                >
                  Wdrażaj
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={writing}
                  onClick={() => chatM.mutate({ message: "jeszcze nie" })}
                >
                  Jeszcze nie
                </Button>
              </div>
            </div>
          ) : null}
          <CaptionChatPanel
            caption={caption}
            engage={item.caption_engage || ""}
            fbEnding=""
            igEnding=""
            messages={chatMessages}
            verifyCta={false}
            busy={chatting}
            canUndo={canUndoChat}
            disabled={generating}
            error={
              (chatM.isError && (chatM.error as Error)?.message) ||
              (undoChatM.isError && (undoChatM.error as Error)?.message) ||
              ""
            }
            onCaptionChange={onCaption}
            onVerifyCta={() => undefined}
            onSend={(message) => chatM.mutate({ message })}
            onUndo={() => undoChatM.mutate()}
            undoPending={undoChatM.isPending}
            showCorpus={false}
            showVerify={false}
            title="Czat o treści"
            hint="Najpierw propozycja. Wdrażam dopiero gdy powiesz tak / Wdrażaj. Sam hak nie rusza opisu."
            placeholder={
              caption.trim() || hook.trim()
                ? "Np. „zostaw opis, hak: Mama rzuca przy stole…” albo „pasuje”."
                : "Najpierw Wygeneruj hak + opis po lewej, albo napisz kąt tutaj."
            }
            busyLabel="Myślę…"
          />
        </div>
      }
    />
  );
}
